import { type Change, diffWords } from 'diff';

import type { ClarityMarkdownDiffResult } from './models/clarity-markdown-diff-result.model';
import type {
	ClarityMarkdownDiffChange,
	ClarityMarkdownDiffRange,
	ClarityMarkdownDiffSource,
} from './models/clarity-markdown-diff-source.model';
import { stripFrontmatter } from './strip-frontmatter.util';

/** Brain's `DiffMarkerType`. */
type DiffMarkerType = ClarityMarkdownDiffChange | 'unchanged';

/** Brain's `DiffBlock`: one change value and what it did. */
type DiffBlock = {
	readonly content: string;
	readonly type: DiffMarkerType;
};

type DiffStats = ClarityMarkdownDiffResult['stats'];

const WHITESPACE = /\s+/;

/** Brain's `computeWordDiff`: jsdiff's `diffWords` with no options. */
function computeWordDiff(oldText: string, newText: string): Change[] {
	return diffWords(oldText, newText);
}

function toMarkerType(change: Change): DiffMarkerType {
	if (change.added) {
		return 'added';
	}

	return change.removed ? 'removed' : 'unchanged';
}

/** Brain's `changesToBlocks`. */
function changesToBlocks(changes: readonly Change[]): DiffBlock[] {
	return changes.map(change => ({ content: change.value, type: toMarkerType(change) }));
}

/** Brain's `countWords`: whitespace-separated chunks, so a lone `*` or `.` is a word. */
function countWords(text: string): number {
	return text.split(WHITESPACE).filter(word => word.length > 0).length;
}

/** Brain's statistics loop over the blocks of a computed diff. */
function countStats(blocks: readonly DiffBlock[]): DiffStats {
	let addedWords = 0;
	let removedWords = 0;
	let addedBlocks = 0;
	let removedBlocks = 0;
	let unchangedBlocks = 0;

	for (const block of blocks) {
		const wordCount = countWords(block.content);
		if (block.type === 'added') {
			addedWords += wordCount;
			addedBlocks++;
		} else if (block.type === 'removed') {
			removedWords += wordCount;
			removedBlocks++;
		} else {
			unchangedBlocks++;
		}
	}

	return { addedBlocks, removedBlocks, unchangedBlocks, addedWords, removedWords };
}

/**
 * The merged source and its ranges — what replaces brain's `blocksToMarkedMarkdown`: every value concatenated in diff
 * order, unescaped, and one range per added or removed value.
 */
function toDiffSource(blocks: readonly DiffBlock[]): ClarityMarkdownDiffSource {
	const ranges: ClarityMarkdownDiffRange[] = [];
	let source = '';

	for (const block of blocks) {
		const start = source.length;
		source += block.content;
		if (block.type !== 'unchanged') {
			ranges.push({ start, end: source.length, change: block.type });
		}
	}

	return { source, ranges };
}

/**
 * The result for a list of blocks. Brain writes its three edge-case results out by hand; counting their single block
 * gives the same values, so every branch goes through here.
 */
function toResult(blocks: readonly DiffBlock[]): ClarityMarkdownDiffResult {
	const stats = countStats(blocks);

	return { ...toDiffSource(blocks), hasChanges: stats.addedBlocks > 0 || stats.removedBlocks > 0, stats };
}

/** Ported from Clarity `apps/web/src/features/canvas/utils/markdownDiff.ts` (commit `6750a5f43`). */
/**
 * Brain's `computeMarkdownDiff`: jsdiff's `diffWords` over the whole body, with brain's edge cases and statistics. It
 * answers the structured-highlight contract instead of brain's marked string (parity exception 7, SEC-4), so nothing in a
 * file's content can produce a highlight.
 *
 * Pass the raw contents: the frontmatter is stripped here, once, as brain's `FileVersionDiff` strips both sides before its
 * call. The edge cases therefore see the bodies — a side that holds only frontmatter is an empty side.
 *
 * jsdiff is 8.0.3, the first release outside CVE-2026-24001, where brain pins 8.0.2. The two tokenise three characters
 * differently — the soft hyphen, `×` and `÷` — which is parity exception 13.
 */
export function computeClarityMarkdownDiff(
	originalContent: string | null | undefined,
	newContent: string | null | undefined,
): ClarityMarkdownDiffResult {
	const original = stripFrontmatter(originalContent ?? '');
	const updated = stripFrontmatter(newContent ?? '');

	// Brain's edge cases, in brain's order: both sides empty, no original, no new side.
	if (!original && !updated) {
		return toResult([]);
	}

	if (!original) {
		return toResult([{ content: updated, type: 'added' }]);
	}

	if (!updated) {
		return toResult([{ content: original, type: 'removed' }]);
	}

	return toResult(changesToBlocks(computeWordDiff(original, updated)));
}
